import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for debounced search functionality.
 * Encapsulates the pattern of debounced API calls for autocomplete search fields.
 *
 * Supports two modes:
 * 1. Callback mode: searchFn returns results directly (legacy, uses .unwrap())
 * 2. Redux mode: searchFn dispatches action, results come via externalOptions prop
 *
 * @param {Function} searchFn - The search function to call (can dispatch or return results)
 * @param {Object} options - Configuration options
 * @param {number} options.debounceMs - Debounce delay in milliseconds (default: 300)
 * @param {number} options.minLength - Minimum input length before searching (default: 2)
 * @param {Array} options.externalOptions - Search results from Redux state (optional)
 * @param {boolean} options.externalLoading - Loading state from Redux (optional)
 * @returns {Object} - { inputValue, setInputValue, options, loading, reset }
 */
const useDebouncedSearch = (searchFn, {
  debounceMs = 300,
  minLength = 2,
  externalOptions = null,
  externalLoading = null,
} = {}) => {
  const [inputValue, setInputValue] = useState('');
  const [internalOptions, setInternalOptions] = useState([]);
  const [internalLoading, setInternalLoading] = useState(false);

  // Use external options/loading if provided (Redux mode), otherwise use internal state
  const useExternalMode = externalOptions !== null;
  const options = useExternalMode ? externalOptions : internalOptions;
  const loading = useExternalMode ? (externalLoading || false) : internalLoading;

  useEffect(() => {
    // Skip if no search function provided
    if (!searchFn) return;

    // Clear options and loading state if input is too short
    if (inputValue.length < minLength) {
      if (!useExternalMode) {
        setInternalOptions([]);
        setInternalLoading(false);
      }
      return;
    }

    if (useExternalMode) {
      // Redux mode: just call the search function (dispatches action)
      const timer = setTimeout(() => {
        searchFn(inputValue);
      }, debounceMs);
      return () => clearTimeout(timer);
    }

    // Legacy callback mode: expect results from return value
    setInternalLoading(true);
    const timer = setTimeout(async () => {
      try {
        const results = await searchFn(inputValue);
        setInternalOptions(results || []);
      } catch (error) {
        console.error('Search error:', error);
        setInternalOptions([]);
      } finally {
        setInternalLoading(false);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [inputValue, searchFn, debounceMs, minLength, useExternalMode]);

  const reset = useCallback(() => {
    setInputValue('');
    setInternalOptions([]);
    setInternalLoading(false);
  }, []);

  return {
    inputValue,
    setInputValue,
    options,
    loading,
    reset,
  };
};

export default useDebouncedSearch;
